import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import NEWSLETTER_RESULT from '../../services/newsletterSubscriptionResult';
import { EMAIL_PATTERN } from '../../services/isValidEmail';
import brandSurfaceInk from '../../services/brandSurfaceInk';
import { pickForHighContrast } from '../highContrastSx';

export const NEWSLETTER_STATUS = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: NEWSLETTER_RESULT.SUCCESS,
    INVALID: NEWSLETTER_RESULT.INVALID_EMAIL,
    ERROR: NEWSLETTER_RESULT.ERROR,
});

const BANNER_SX = {
    bgcolor: 'brandSurface.main',
    color: 'brandSurface.contrastText',
};

const CONTAINER_SX = {
    py: { xs: 3, md: 4 },
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { md: 'flex-start' },
    justifyContent: 'space-between',
    gap: 2,
};

export const HEADING_SX = theme => ({
    fontSize: { xs: theme.typography.pxToRem(20), md: theme.typography.pxToRem(24) },
    fontWeight: 600,
    lineHeight: 1.33,
    letterSpacing: '-0.01em',
    margin: 0,
    mb: 0.5,
});

export const DESCRIPTION_SX = theme => ({
    fontSize: theme.typography.pxToRem(14),
    color: brandSurfaceInk(theme, 0.7),
    margin: 0,
});

const INTRO_SX = {
    flexGrow: 1,
    pr: { md: 4 },
};

const FORM_SX = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    flexShrink: 0,
    mt: { md: '6px' },
    width: { xs: '100%', md: 'auto' },
};

const FIELD_ROW_SX = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { sm: 'flex-start' },
    gap: 1,
};

export const INPUT_MIN_WIDTH_DEFAULT = 260;
export const INPUT_MIN_WIDTH_ACTIVE = 360;

export const INPUT_SX = theme => ({
    flex: 1,
    transition: theme.transitions.create('min-width', {
        duration: theme.transitions.duration.shorter,
    }),
    '& .MuiOutlinedInput-root': {
        bgcolor: alpha(theme.palette.brandSurface.contrastText, 0.1),
        color: 'brandSurface.contrastText',
        '& fieldset': { borderColor: brandSurfaceInk(theme, 0.3) },
        '&:hover fieldset': { borderColor: brandSurfaceInk(theme, 0.5) },
        '&.Mui-focused fieldset': { borderColor: 'brandSurface.contrastText' },
        '&.Mui-error fieldset, &.Mui-error.Mui-focused fieldset': { borderColor: 'brandSurface.error' },
    },
    '& .MuiInputBase-input': {
        fontSize: { xs: theme.typography.pxToRem(16), md: theme.typography.pxToRem(14) },
        '&::placeholder': { color: brandSurfaceInk(theme, 0.6), opacity: 1 },
    },
    '& .MuiFormHelperText-root': {
        ml: 0,
        color: brandSurfaceInk(theme, 0.85),
        '&.Mui-error': { color: 'brandSurface.error' },
    },
});

const SUBMIT_SX = theme => ({
    bgcolor: 'brandSurface.contrastText',
    color: 'brandSurface.main',
    fontWeight: 600,
    flexShrink: 0,
    px: 3,
    textTransform: 'none',
    '&:hover': {
        bgcolor: 'brandSurface.actionHover',
        color: 'brandSurface.main',
        '@media (hover: none)': pickForHighContrast({ bgcolor: 'brandSurface.contrastText', boxShadow: 'none' }, {}),
    },
    '&.Mui-disabled': { bgcolor: alpha(theme.palette.brandSurface.contrastText, 0.4), color: 'brandSurface.main' },
});

export const CONSENT_SX = theme => ({
    mt: 1.5,
    mb: 0,
    maxWidth: 520,
    fontSize: theme.typography.pxToRem(12),
    color: brandSurfaceInk(theme, 0.7),
    lineHeight: 1.4,
});

export const CONSENT_LINK_SX = theme => ({
    color: brandSurfaceInk(theme, 0.85),
    textDecorationColor: 'currentColor',
    '&:hover, &:focus, &:focus-visible': {
        color: 'brandSurface.contrastText',
        textDecorationColor: 'currentColor',
    },
});

export const SUCCESS_MESSAGE_SX = {
    '& .MuiFormHelperText-root': { color: 'brandSurface.success' },
};

const ERROR_HELPER_TEXT_PROPS = { role: 'alert', 'data-testid': 'redesign-footer-newsletter-error' };
const SUCCESS_HELPER_TEXT_PROPS = { role: 'status', 'data-testid': 'redesign-footer-newsletter-status' };

function getHelperTextProps(hasError, isSuccess) {
    if (hasError) {
        return ERROR_HELPER_TEXT_PROPS;
    }

    if (isSuccess) {
        return SUCCESS_HELPER_TEXT_PROPS;
    }

    return undefined;
}

function NewsletterBanner({
    heading,
    description,
    placeholder,
    submitLabel,
    invalidEmailLabel,
    loadingLabel,
    successLabel,
    errorLabel,
    consentLabel,
    privacyPolicyLabel,
    privacyPolicyHref,
    status = NEWSLETTER_STATUS.IDLE,
    onSubmit = null,
}) {
    const [email, setEmail] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isFieldActive, setIsFieldActive] = useState(false);
    const [statusFeedbackDismissed, setStatusFeedbackDismissed] = useState(false);

    const isLoading = status === NEWSLETTER_STATUS.LOADING;
    const showStatusFeedback = !statusFeedbackDismissed;
    const isSuccess = showStatusFeedback && status === NEWSLETTER_STATUS.SUCCESS;
    const showsInvalid = showStatusFeedback && status === NEWSLETTER_STATUS.INVALID;
    const showsGenericError = showStatusFeedback && status === NEWSLETTER_STATUS.ERROR;
    const fieldErrorMessage = validationError
        || (showsInvalid ? invalidEmailLabel : '')
        || (showsGenericError ? errorLabel : '');
    const hasFieldError = Boolean(fieldErrorMessage);
    const feedbackMessage = fieldErrorMessage || (isSuccess ? successLabel : '');
    const isFieldEngaged = isFieldActive || email.trim().length > 0;

    useEffect(() => {
        setStatusFeedbackDismissed(false);
        if (status === NEWSLETTER_STATUS.SUCCESS) {
            setEmail('');
        }
    }, [status]);

    const handleChange = (event) => {
        setEmail(event.target.value);
        setStatusFeedbackDismissed(true);
        if (validationError) {
            setValidationError('');
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (isLoading) {
            return;
        }

        const trimmedEmail = email.trim();
        if (!EMAIL_PATTERN.test(trimmedEmail)) {
            setValidationError(invalidEmailLabel);
            return;
        }

        setValidationError('');
        if (onSubmit) {
            onSubmit(trimmedEmail);
        }
    };

    return (
        <Box sx={BANNER_SX} data-testid="redesign-footer-newsletter">
            <Container sx={CONTAINER_SX}>
                <Box sx={INTRO_SX}>
                    <Box component="h2" sx={HEADING_SX}>{heading}</Box>
                    <Box component="p" sx={DESCRIPTION_SX}>{description}</Box>
                    <Collapse in={isFieldEngaged} unmountOnExit>
                        <Box
                            component="p"
                            sx={CONSENT_SX}
                            data-testid="redesign-footer-newsletter-consent-note"
                        >
                            {`${consentLabel} `}
                            <Link
                                href={privacyPolicyHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={CONSENT_LINK_SX}
                                data-testid="redesign-footer-newsletter-privacy-link"
                            >
                                {privacyPolicyLabel}
                            </Link>
                        </Box>
                    </Collapse>
                </Box>
                <Box component="form" onSubmit={handleSubmit} sx={FORM_SX} noValidate>
                    <Box sx={FIELD_ROW_SX}>
                        <TextField
                            type="email"
                            value={email}
                            onChange={handleChange}
                            onFocus={() => setIsFieldActive(true)}
                            onBlur={() => setIsFieldActive(false)}
                            placeholder={placeholder}
                            aria-label={placeholder}
                            error={hasFieldError}
                            helperText={feedbackMessage || ' '}
                            size="small"
                            fullWidth
                            disabled={isLoading}
                            inputProps={{ 'data-testid': 'redesign-footer-newsletter-input' }}
                            FormHelperTextProps={getHelperTextProps(hasFieldError, isSuccess)}
                            sx={[
                                INPUT_SX,
                                isSuccess && SUCCESS_MESSAGE_SX,
                                {
                                    minWidth: {
                                        md: isFieldEngaged ? INPUT_MIN_WIDTH_ACTIVE : INPUT_MIN_WIDTH_DEFAULT,
                                    },
                                },
                            ]}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={SUBMIT_SX}
                            data-testid="redesign-footer-newsletter-submit"
                        >
                            <span>{isLoading ? loadingLabel : submitLabel}</span>
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

NewsletterBanner.propTypes = {
    heading: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    submitLabel: PropTypes.string.isRequired,
    invalidEmailLabel: PropTypes.string.isRequired,
    loadingLabel: PropTypes.string.isRequired,
    successLabel: PropTypes.string.isRequired,
    errorLabel: PropTypes.string.isRequired,
    consentLabel: PropTypes.string.isRequired,
    privacyPolicyLabel: PropTypes.string.isRequired,
    privacyPolicyHref: PropTypes.string.isRequired,
    status: PropTypes.oneOf(Object.values(NEWSLETTER_STATUS)),
    onSubmit: PropTypes.func,
};

export default NewsletterBanner;
